/**
 * Wave 42 · Admin endpoint response contracts (Zod schemas).
 *
 * These schemas pin the wire shape that each admin endpoint MUST return.
 * Any divergence (missing field, wrong type, silently-empty object) is
 * caught at CI time before it reaches the frontend renderer.
 *
 * Bug motivation: Wave 39 shipped a procurement-autopilot endpoint that
 * read `parsedPayload.evidence` — always undefined because the service
 * spreads evidence at the top level.  The frontend saw `evidence: {}`
 * and rendered "Dados ainda sendo coletados" for every issue.  One
 * pinning test would have caught this before merge.
 *
 * Covered endpoints (priority order):
 *   1. GET /v1/admin/autonomy/procurement-autopilot/issues
 *   2. GET /v1/admin/health/snapshot
 *   3. GET /v1/admin/health/crons
 *   4. GET /v1/admin/health/self-heal
 */

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// 1. Procurement Autopilot — /v1/admin/autonomy/procurement-autopilot/issues
//    Wave 39.B fix: evidence is spread at payload top-level by the service;
//    the route recombines it into { issue_kind, action_hint, evidence }.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Evidence for an rfq_stale issue.
 * Fields populated by `raiseProcurementIssueProposal` in the service.
 */
export const RfqStaleEvidenceSchema = z.object({
  rfq_titulo: z.string().min(1),
  dias_aberta: z.number().int().nonnegative(),
  // Optional fields that may be present
  fornecedores_convidados: z.number().int().nonnegative().optional(),
  fornecedores_responderam: z.number().int().nonnegative().optional(),
  menor_preco_brl: z.string().optional(),
  fornecedor_menor_preco: z.string().optional(),
  aberta_em: z.string().optional(),
  entity_id: z.string().optional(),
  entity_type: z.string().optional(),
  project_id: z.string().optional(),
});

export const LowStockEvidenceSchema = z.object({
  description: z.string().min(1),
  unit: z.string().optional(),
  current_qty: z.number().optional(),
  minimum_qty: z.number().optional(),
  last_movement: z.string().optional(),
  project_id: z.string().optional(),
});

export const MissingSupplierEvidenceSchema = z.object({
  stage_name: z.string().min(1),
  planned_start_date: z.string().optional(),
  project_id: z.string().optional(),
});

export const ContractExpiringEvidenceSchema = z.object({
  supplier_name: z.string().min(1),
  contract_expires_at: z.string().optional(),
  days_until_expiry: z.number().int().optional(),
  project_id: z.string().optional(),
});

/**
 * Generic evidence record — covers unknown issue_kinds.
 * Keys are strings; values are string | number | null.
 */
export const GenericEvidenceSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.null(), z.boolean()]),
);

/**
 * The payload object nested inside each procurement-autopilot issue row.
 * After the Wave 39.B fix, `evidence` is always an object (may be empty
 * for legacy rows; MUST NOT be undefined).
 */
export const ProcurementAutopilotPayloadSchema = z.object({
  issue_kind: z.union([
    z.enum(['low_stock', 'missing_supplier', 'rfq_stale', 'contract_expiring']),
    z.string(), // future kinds
  ]).nullable(),
  action_hint: z.string().nullable(),
  evidence: GenericEvidenceSchema,
});

/**
 * One row from the /issues endpoint.
 * NOTE: the response wraps rows in `{ data: { rows: [...] } }`.
 */
export const ProcurementAutopilotIssueSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  subtitle: z.string().nullable().optional(),
  payload: ProcurementAutopilotPayloadSchema,
  status: z.string(),
  tier: z.number().int().optional().nullable(),
  created_at: z.string().nullable().optional(),
  decided_at: z.string().nullable().optional(),
  decided_by: z.string().nullable().optional(),
});

export type ProcurementAutopilotIssue = z.infer<typeof ProcurementAutopilotIssueSchema>;

/**
 * Full response shape for GET /v1/admin/autonomy/procurement-autopilot/issues.
 * Wrapped: { data: { rows: ProcurementAutopilotIssue[] } }
 */
export const ProcurementAutopilotIssuesResponseSchema = z.object({
  data: z.object({
    rows: z.array(ProcurementAutopilotIssueSchema),
  }),
});

// ─────────────────────────────────────────────────────────────────────────────
// 2. Health snapshot — /v1/admin/health/snapshot
//    Used by AdminSaudePage.tsx (HealthModule interface must match).
// ─────────────────────────────────────────────────────────────────────────────

export const HealthModuleStatusSchema = z.enum(['ok', 'degraded', 'down', 'unknown']);
export type HealthModuleStatus = z.infer<typeof HealthModuleStatusSchema>;

export const HealthModuleCategorySchema = z.enum(['core', 'data', 'comms', 'integration', 'jobs']);

export const HealthModuleSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  category: HealthModuleCategorySchema,
  status: HealthModuleStatusSchema,
  signal: z.string(), // human-readable signal string — MUST be non-empty
  lastSeenIssueAt: z.string().nullable(),
  link: z.string().nullable(),
});

export type HealthModule = z.infer<typeof HealthModuleSchema>;

export const HealthAggregateSchema = z.object({
  status: z.enum(['ok', 'degraded', 'down']),
  downCount: z.number().int().nonnegative(),
  degradedCount: z.number().int().nonnegative(),
  totalModules: z.number().int().positive(),
});

/**
 * Full response shape for GET /v1/admin/health/snapshot.
 * Wrapped: { data: { modules, aggregate, generatedAt } }
 */
export const HealthSnapshotResponseSchema = z.object({
  data: z.object({
    modules: z.array(HealthModuleSchema).min(1),
    aggregate: HealthAggregateSchema,
    generatedAt: z.string().min(1),
  }),
});

export type HealthSnapshotResponse = z.infer<typeof HealthSnapshotResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// 3. Cron health — /v1/admin/health/crons
//    Used by AdminSaudePage.tsx expanded cron section.
// ─────────────────────────────────────────────────────────────────────────────

export const CronJobStatusSchema = z.enum(['ok', 'warn', 'critical', 'stale', 'unknown']);
export type CronJobStatus = z.infer<typeof CronJobStatusSchema>;

export const CronJobSchema = z.object({
  jobName: z.string().min(1),
  status: CronJobStatusSchema,
  lastSuccessAt: z.string().nullable(),
  lastFailureAt: z.string().nullable(),
  successCount7d: z.number().int().nonnegative(),
  failureCount7d: z.number().int().nonnegative(),
  successRate7d: z.number().nullable(),
  lastError: z.string().nullable(),
  updatedAt: z.string().nullable(),
  hoursSinceSuccess: z.number().nullable(),
});

export type CronJob = z.infer<typeof CronJobSchema>;

export const CronAggregateSchema = z.object({
  total: z.number().int().nonnegative(),
  ok: z.number().int().nonnegative(),
  warn: z.number().int().nonnegative(),
  critical: z.number().int().nonnegative(),
  stale: z.number().int().nonnegative(),
  unknown: z.number().int().nonnegative(),
});

/**
 * Full response shape for GET /v1/admin/health/crons.
 * Wrapped: { data: { jobs, aggregate, generatedAt } }
 * Note: when the cron_health_summary table is absent, the endpoint
 * returns jobs:[] with a `note` field — the schema tolerates that.
 */
export const CronHealthResponseSchema = z.object({
  data: z.object({
    jobs: z.array(CronJobSchema),
    aggregate: CronAggregateSchema,
    generatedAt: z.string().min(1),
    note: z.string().optional(),
  }),
});

export type CronHealthResponse = z.infer<typeof CronHealthResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// 4. Self-heal summary — /v1/admin/health/self-heal
//    Used by AdminSaudePage.tsx self-heal section.
// ─────────────────────────────────────────────────────────────────────────────

export const SelfHealRunSchema = z.object({
  id: z.number().int().positive(),
  triggeredAt: z.string().min(1),
  triggerKind: z.string().min(1),
  actionKind: z.string().min(1),
  tier: z.number().int().nullable(),
  outcome: z.enum(['ok', 'failed', 'skipped']),
  durationMs: z.number().int().nonnegative().nullable(),
  errorText: z.string().nullable(),
  context: z.record(z.string(), z.unknown()),
  proposalId: z.string().nullable(),
  recoveredBy: z.string().nullable(),
});

export type SelfHealRun = z.infer<typeof SelfHealRunSchema>;

export const SelfHealByActionSchema = z.object({
  actionKind: z.string().min(1),
  ok: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});

export const SelfHealSummarySchema = z.object({
  windowHours: z.number().int().positive(),
  attempts: z.number().int().nonnegative(),
  ok: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  recoveryPct: z.number().min(0).max(100),
  byAction: z.array(SelfHealByActionSchema),
  recent: z.array(SelfHealRunSchema),
});

/**
 * Full response shape for GET /v1/admin/health/self-heal.
 * Wrapped: { data: SelfHealSummary }
 */
export const SelfHealResponseSchema = z.object({
  data: SelfHealSummarySchema,
});

export type SelfHealResponse = z.infer<typeof SelfHealResponseSchema>;
